import React from 'react';
import { useTranslation } from 'react-i18next';

import { OperationProblem } from '../../../../../gamma-api';
import IbTypography from '../../IbTypography';
import IbModal from '../../IbModal';
import IbIcon from '../../IbIcon';
import IbButton from '../../IbButton';
import { StatusInfoState } from '../types';

import './index.less';

import {
  BLOCK_CLASS_NAME,
  CONTENT_CLASS_NAME,
  DETAILS_CLASS_NAME,
  MAIN_CLASS_NAME,
  MESSAGE_CLASS_NAME,
  TITLE_CLASS_NAME,
} from './const';

export interface IStatusModalProps {
  visible: boolean;
  rolling: boolean;
  title?: string;
  subtitle?: string;
  description?: string;
  problem?: OperationProblem | null;
  status?: StatusInfoState;
  onRestart?: () => void;
  onClose?: () => void;
}

const StatusModal: React.FC<IStatusModalProps> = ({
  visible,
  rolling,
  title,
  subtitle,
  description,
  problem,
  status,
  onRestart,
  onClose,
}) => {
  const { t } = useTranslation();

  const onRestartClick = () => {
    !rolling && onRestart?.();
  };

  const onCloseClick = () => {
    onClose?.();
  };

  const onModalCancel = () => {
    onClose?.();
  };

  const renderSubtitle = () => {
    if (!subtitle) {
      return null;
    }

    return (
      <IbTypography.Paragraph strong error={status === StatusInfoState.Error}>
        {subtitle}
      </IbTypography.Paragraph>
    );
  };

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    return (
      <IbTypography.Paragraph error={status === StatusInfoState.Error} type="secondary">
        {description}
      </IbTypography.Paragraph>
    );
  };

  const renderMessage = () => {
    if (!problem) {
      return null;
    }

    const title = problem.details.title || t('Status modal: Message');
    const detail = problem.details.detail || '';

    const onCopyClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      navigator.clipboard.writeText(detail).finally();
    };

    return (
      <div className={`${BLOCK_CLASS_NAME} ${MESSAGE_CLASS_NAME}`}>
        <div className={TITLE_CLASS_NAME}>
          <IbTypography.Paragraph>{title}</IbTypography.Paragraph>
          <IbButton disabled={rolling} icon={<IbIcon iconName="copy-one" />} type="link" onClick={onCopyClick} />
        </div>
        <pre>{detail}</pre>
      </div>
    );
  };

  const renderDetails = () => {
    if (!problem) {
      return null;
    }

    const extensions = problem.details.extensions as { traceback?: string };
    const traceback = extensions.traceback || '';

    const onCopyClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      navigator.clipboard.writeText(traceback).finally();
    };

    return (
      <div className={`${BLOCK_CLASS_NAME} ${DETAILS_CLASS_NAME}`}>
        <div className={TITLE_CLASS_NAME}>
          <IbTypography.Paragraph>{t('Status modal: Details')}</IbTypography.Paragraph>
          <IbButton icon={<IbIcon iconName="copy-one" />} type="link" onClick={onCopyClick} />
        </div>
        <pre>{traceback}</pre>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        {renderSubtitle()}
        {renderDescription()}
        {renderMessage()}
        {renderDetails()}
      </div>
    );
  };

  const renderButtons = () => {
    return (
      <>
        <IbButton onClick={onRestartClick}>{t('Status modal: Restart')}</IbButton>
        <IbButton type="secondary" onClick={onCloseClick}>
          {t('Status modal: Close')}
        </IbButton>
      </>
    );
  };

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderButtons()}
      title={title || ''}
      visible={visible}
      onCancel={onModalCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default StatusModal;
